import isPropValid from '@emotion/is-prop-valid';
import React, { type FunctionComponent, type ReactNode, useMemo } from 'react';
import { type DefaultTheme, ThemeProvider as Provider, StyleSheetManager, useTheme } from 'styled-components';

import { makeMediaQueries } from './breakpoints';
import { designTokens } from './design-tokens';
import { theme as defaultTheme, type ThemeOverrides } from './theme';

// NOTE: the `DefaultTheme` type is extended here: <project>/src/iziwork-styled-components.d.ts

type Props = {
  children: ReactNode;
  theme?: ThemeOverrides;
};

// Function to mimic v5 behavior
function shouldForwardProp(propName: string, target: string | React.ComponentType<any>) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}

export const ThemeProvider: FunctionComponent<Props> = ({ children, theme = defaultTheme }) => {
  const themeWithDesignTokens = useMemo(
    (): DefaultTheme => ({
      ...theme,
      designTokens: { ...designTokens(theme), ...theme.designTokens },
      deviceMediaQueries: makeMediaQueries(theme.devices),
    }),
    [theme],
  );

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
      <Provider theme={themeWithDesignTokens}>{children}</Provider>
    </StyleSheetManager>
  );
};

export { useTheme };
